import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import type { Student } from '@/types';
import { formatDate } from '@/lib/datetime';

interface StudentStatusSectionProps {
  student: Student;
  onUpdated?: (student: Student) => void;
}

export default function StudentStatusSection({ student, onUpdated }: StudentStatusSectionProps) {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const isEnded = student.status === 'ended';

  const handleEnd = async () => {
    setBusy(true);
    setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const { data, error: updateError } = await supabase
        .from('students')
        .update({ status: 'ended', ended_at: today })
        .eq('id', student.id)
        .select()
        .maybeSingle();
      if (updateError) throw updateError;
      await supabase.from('parent_shares').delete().eq('student_id', student.id);
      if (data) onUpdated?.(data as Student);
      setConfirmOpen(false);
    } catch {
      setError('수업 종료 처리에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  const handleResume = async () => {
    setBusy(true);
    setError('');
    try {
      const { data, error: updateError } = await supabase
        .from('students')
        .update({ status: 'active', ended_at: null })
        .eq('id', student.id)
        .select()
        .maybeSingle();
      if (updateError) throw updateError;
      if (data) onUpdated?.(data as Student);
    } catch {
      setError('수업 재개 처리에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setBusy(false);
    }
  };

  // 학생과 모든 연관 기록(수업일지·진도·시험·교습비·보충·학부모 공유)을 영구 삭제해요.
  const handlePermanentDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      const { error: deleteError } = await supabase.from('students').delete().eq('id', student.id);
      if (deleteError) throw deleteError;
      navigate('/students');
    } catch {
      setError('학생 영구 삭제에 실패했어요. 잠시 후 다시 시도해주세요.');
      setDeleting(false);
    }
  };

  return (
    <section className="mt-6 rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                isEnded ? 'bg-accent-100 text-accent-700' : 'bg-background-200 text-foreground-600'
              }`}
            >
              <i className={isEnded ? 'ri-user-unfollow-line' : 'ri-settings-3-line'}></i>
            </span>
            수업 관리
          </h2>
          <p className="mt-1.5 text-xs leading-relaxed text-foreground-500">
            {isEnded
              ? `종료일 ${formatDate(student.ended_at)} · 수업을 다시 시작하려면 ‘수업 재개’를, 기록까지 완전히 지우려면 ‘영구 삭제’를 눌러주세요.`
              : '더 이상 수업하지 않는 학생은 종료 처리할 수 있어요. 종료하면 학부모 공유 링크도 함께 중지돼요.'}
          </p>
        </div>
        {isEnded ? (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleResume}
              disabled={busy || deleting}
              className="inline-flex items-center gap-1.5 rounded-md border border-primary-300 bg-background-50 px-4 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:opacity-50 whitespace-nowrap"
            >
              {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-restart-line"></i>}
              수업 재개
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen((v) => !v)}
              disabled={busy || deleting}
              className="inline-flex items-center gap-1.5 rounded-md border border-accent-200 bg-background-50 px-4 py-2 text-sm font-semibold text-accent-700 transition-colors hover:bg-accent-50 disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-delete-bin-6-line"></i>
              영구 삭제
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-md border border-accent-200 bg-background-50 px-4 py-2 text-sm font-semibold text-accent-700 transition-colors hover:bg-accent-50 whitespace-nowrap"
          >
            <i className="ri-user-unfollow-line"></i>
            수업 종료
          </button>
        )}
      </div>

      {confirmOpen && (
        <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-3.5">
          <p className="text-xs font-medium leading-relaxed text-accent-900">
            {student.name} 학생을 ‘더 이상 수업 안 함’으로 처리할까요? 수업 중지와 함께 학부모 공유
            링크도 함께 중지돼요. 기존 기록은 그대로 보관됩니다.
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={handleEnd}
              disabled={busy}
              className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
            >
              {busy && <i className="ri-loader-4-line animate-spin"></i>}
              수업 종료
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {deleteOpen && (
        <div className="mt-4 rounded-lg border border-accent-300 bg-accent-50 p-3.5">
          <p className="flex items-center gap-1.5 text-xs font-bold text-accent-900">
            <i className="ri-error-warning-line"></i>
            {student.name} 학생을 완전히 삭제할까요?
          </p>
          <p className="mt-1 text-xs leading-relaxed text-accent-800">
            수업일지·진도·시험 성적·교습비·보충 수업·학부모 공유 링크까지 <strong>모든 기록이 영구히
            삭제</strong>되며 되돌릴 수 없어요.
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={handlePermanentDelete}
              disabled={deleting}
              className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
            >
              {deleting ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-delete-bin-6-line"></i>
              )}
              영구 삭제
            </button>
            <button
              type="button"
              onClick={() => setDeleteOpen(false)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              취소
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}
    </section>
  );
}